import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarEvent } from '@demind-inc/core';
import { cloneDeep } from 'lodash';

import { calendarApi } from '../api';
import { supplyCreateEventOption } from '../helpers';
import { CalendarEventModifyOption } from '../types';
import dayjs from 'dayjs';

interface UseCreateAllDayCalendarEventParams {
  calendarId: string;
  userId: string;
  newEventOption: CalendarEventModifyOption;
}

export function useCreateAllDayCalendarEvent() {
  const queryClient = useQueryClient();

  const createAllDayCalendarEventMutation = useMutation({
    mutationFn: ({ calendarId, userId, newEventOption }: UseCreateAllDayCalendarEventParams) => {
      return calendarApi.createAllDayCalendarEvent(calendarId, userId, {
        newEventOption,
      });
    },
    onMutate: async ({ calendarId, newEventOption }: UseCreateAllDayCalendarEventParams) => {
      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.allDayEvents'] });
      queryClient.setQueriesData<CalendarEvent[]>(
        { queryKey: ['lifestack.calendar.allDayEvents'] },
        (prevEvents) => {
          const suppliedNewEvent: CalendarEvent = {
            ...supplyCreateEventOption(calendarId, {
              ...newEventOption,
              // for the glitch for the wrong event lenght while creating
              end: {
                ...newEventOption.end,
                date: dayjs(newEventOption.end?.date).toISOString(),
              },
            }),
            isAllDay: true,
          };

          return cloneDeep([...(prevEvents ?? []), suppliedNewEvent]);
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.allDayEvents'] });
    },
  });

  return {
    createAllDayCalendarEvent: createAllDayCalendarEventMutation.mutateAsync,
    isCreating: createAllDayCalendarEventMutation.isPending,
    ...createAllDayCalendarEventMutation,
  };
}
